/* eslint-disable no-restricted-syntax */
import type { RGBA } from '@onxmaps/markups-web'
import Color from '@/lib/color'
import waypointOrange from '@/assets/icons/waypoint-base/orange.svg?raw'
import waypointOrangeSel from '@/assets/icons/waypoint-base/orange-selected.svg?raw'
import waypointWhite from '@/assets/icons/waypoint-base/white.svg?raw'
import waypointWhiteSel from '@/assets/icons/waypoint-base/white-selected.svg?raw'
import waypointBlue from '@/assets/icons/waypoint-base/blue.svg?raw'
import waypointBlueSel from '@/assets/icons/waypoint-base/blue-selected.svg?raw'
import waypointCyan from '@/assets/icons/waypoint-base/cyan.svg?raw'
import waypointCyanSel from '@/assets/icons/waypoint-base/cyan-selected.svg?raw'
import waypointBlack from '@/assets/icons/waypoint-base/black.svg?raw'
import waypointBlackSel from '@/assets/icons/waypoint-base/black-selected.svg?raw'
import waypointPurple from '@/assets/icons/waypoint-base/purple.svg?raw'
import waypointPurpleSel from '@/assets/icons/waypoint-base/purple-selected.svg?raw'
import waypointYellow from '@/assets/icons/waypoint-base/yellow.svg?raw'
import waypointYellowSel from '@/assets/icons/waypoint-base/yellow-selected.svg?raw'
import waypointRed from '@/assets/icons/waypoint-base/red.svg?raw'
import waypointRedSel from '@/assets/icons/waypoint-base/red-selected.svg?raw'
import waypointBrown from '@/assets/icons/waypoint-base/brown.svg?raw'
import waypointBrownSel from '@/assets/icons/waypoint-base/brown-selected.svg?raw'
import waypointGreen from '@/assets/icons/waypoint-base/green.svg?raw'
import waypointGreenSel from '@/assets/icons/waypoint-base/green-selected.svg?raw'

// Dots
import waypointOrangeDot from '@/assets/icons/waypoint-base/dots/orange.svg?raw'
import waypointWhiteDot from '@/assets/icons/waypoint-base/dots/white.svg?raw'
import waypointBlueDot from '@/assets/icons/waypoint-base/dots/blue.svg?raw'
import waypointCyanDot from '@/assets/icons/waypoint-base/dots/cyan.svg?raw'
import waypointBlackDot from '@/assets/icons/waypoint-base/dots/black.svg?raw'
import waypointPurpleDot from '@/assets/icons/waypoint-base/dots/purple.svg?raw'
import waypointYellowDot from '@/assets/icons/waypoint-base/dots/yellow.svg?raw'
import waypointRedDot from '@/assets/icons/waypoint-base/dots/red.svg?raw'
import waypointBrownDot from '@/assets/icons/waypoint-base/dots/brown.svg?raw'
import waypointGreenDot from '@/assets/icons/waypoint-base/dots/green.svg?raw'

/** Specialty Waypoint Colors */
import waypointLeupold from '@/assets/icons/waypoint-base/leupold.svg?raw'
import waypointLeupoldSel from '@/assets/icons/waypoint-base/leupold-selected.svg?raw'
import waypointSigSauer from '@/assets/icons/waypoint-base/sig-sauer.svg?raw'
import waypointSigSauerSel from '@/assets/icons/waypoint-base/sig-sauer-selected.svg?raw'
import waypointHorus from '@/assets/icons/waypoint-base/horus.svg?raw'
import waypointHorusSel from '@/assets/icons/waypoint-base/horus-selected.svg?raw'
import waypointRevic from '@/assets/icons/waypoint-base/revic.svg?raw'
import waypointRevicSel from '@/assets/icons/waypoint-base/revic-selected.svg?raw'
import waypointLeica from '@/assets/icons/waypoint-base/leica.svg?raw'
import waypointLeicaSel from '@/assets/icons/waypoint-base/leica-selected.svg?raw'

/** Specialty colors that should not be shown in markup color selectors **/
const SPECIALTY_COLORS = ['leupold', 'sig-sauer', 'horus', 'revic', 'leica']

export interface MarkupColor {
  key: string
  label: string
  color: Color
  rgba: string
  circleColor: Color
  circleColorRgba: string
  waypoint: boolean
  line: boolean
  shapeStroke: boolean
  waypointSvg: string | null
  waypointSelectedSvg: string | null
  waypointLowZoomSvg: string | null
  tint: 'light' | 'dark'
  dbValue: string
}

export interface ShapeFillColor {
  key: string
  label: string
  opaqueColor: Color
  opaqueRgba: string
  fillColor: Color
  fillRgba: string
  dbValue: string
}

/**
 * Markup colors used for waypoints, lines, and shape strokes. Note that not all colors are used for all markup types.
 * You may either filter the list based on the 'waypoint', 'line', and 'shapeStroke' boolean properties, or use the
 * WAYPOINT_COLORS, LINE_COLORS, and SHAPE_STROKE_COLORS constants instead. Note that area fill colors are defined
 * separately in SHAPE_FILL_COLORS since they do not overlap.
 *
 * Each entry in the array contains the following properties:
 *
 *   - key {String}: Unique key identifying the color. Can be used to look up the color in MARKUP_COLOR_MAP.
 *   - label {String}: Color name (title case).
 *   - color {Color}: Color value, represented as a Color object (see lib/color.ts)
 *   - rgba {String}: Color rgba string, suitable for use in CSS
 *   - circleColor {Color}: For waypoints, defines the corresponding fill color of the inner circle of the
 *      waypoint icon. This is also sometimes used as the border color when representing the color in the UI.
 *   - circleColorRgba {String}: Circle color represented as an RGBA string (suitable for use in CSS).
 *   - waypoint {Boolean}: True if this color is used for waypoints.
 *   - line {Boolean}: True if this color is used for line strokes.
 *   - shapeStroke {Boolean}: True if this color is used for area strokes.
 *   - waypointSvg {String}: Inline SVG for the default (unselected) state of a waypoint that uses this color.
 *   - waypointSelectedSvg {String}: Inline SVG for the selected state of a waypoint that uses this color.
 *   - tint {String}: Tint to use for inner text or icons that use this color (either 'light' or 'dark'). Most
 *      colors will default to using a 'light' tint, but brighter colors such as white or yellow will have a
 *      'dark' tint.
 *   - dbValue {String}: How this color is represented on the backend. This must match exactly when
 *      saving/parsing a markup.
 */
export const MARKUP_COLORS: MarkupColor[] = [
  {
    key: 'orange',
    label: 'Orange',
    color: new Color(255, 51, 0, 1.0),
    rgba: '',
    circleColor: new Color(181, 52, 43, 1.0),
    circleColorRgba: '',
    waypoint: true,
    line: true,
    shapeStroke: true,
    waypointSvg: waypointOrange,
    waypointSelectedSvg: waypointOrangeSel,
    waypointLowZoomSvg: waypointOrangeDot,
    tint: 'light',
    dbValue: '255,51,0,1',
  },
  {
    key: 'blue',
    label: 'Blue',
    color: new Color(8, 122, 255, 1.0),
    rgba: '',
    circleColor: new Color(0, 86, 187, 1.0),
    circleColorRgba: '',
    waypoint: true,
    line: true,
    shapeStroke: true,
    waypointSvg: waypointBlue,
    waypointSelectedSvg: waypointBlueSel,
    waypointLowZoomSvg: waypointBlueDot,
    tint: 'light',
    dbValue: '8,122,255,1',
  },
  {
    key: 'cyan',
    label: 'Cyan',
    color: new Color(0, 255, 255, 1.0),
    rgba: '',
    circleColor: new Color(0, 153, 153, 1.0),
    circleColorRgba: '',
    waypoint: true,
    line: true,
    shapeStroke: true,
    waypointSvg: waypointCyan,
    waypointSelectedSvg: waypointCyanSel,
    waypointLowZoomSvg: waypointCyanDot,
    tint: 'dark',
    dbValue: '0,255,255,1',
  },
  {
    key: 'green',
    label: 'Green',
    color: new Color(132, 212, 0, 1.0),
    rgba: '',
    circleColor: new Color(101, 182, 4, 1.0),
    circleColorRgba: '',
    waypoint: true,
    line: true,
    shapeStroke: true,
    waypointSvg: waypointGreen,
    waypointSelectedSvg: waypointGreenSel,
    waypointLowZoomSvg: waypointGreenDot,
    tint: 'light',
    dbValue: '132,212,0,1',
  },
  {
    key: 'black',
    label: 'Black',
    color: new Color(0, 0, 0, 1.0),
    rgba: '',
    circleColor: new Color(102, 102, 102, 1.0),
    circleColorRgba: '',
    waypoint: true,
    line: true,
    shapeStroke: true,
    waypointSvg: waypointBlack,
    waypointSelectedSvg: waypointBlackSel,
    waypointLowZoomSvg: waypointBlackDot,
    tint: 'light',
    dbValue: '0,0,0,1',
  },
  {
    key: 'white',
    label: 'White',
    color: new Color(255, 255, 255, 1.0),
    rgba: '',
    circleColor: new Color(51, 51, 51, 1.0),
    circleColorRgba: '',
    waypoint: true,
    line: true,
    shapeStroke: true,
    waypointSvg: waypointWhite,
    waypointSelectedSvg: waypointWhiteSel,
    waypointLowZoomSvg: waypointWhiteDot,
    tint: 'dark',
    dbValue: '255,255,255,1',
  },
  {
    key: 'purple',
    label: 'Purple',
    color: new Color(128, 0, 128, 1.0),
    rgba: '',
    circleColor: new Color(255, 0, 255, 1.0),
    circleColorRgba: '',
    waypoint: true,
    line: true,
    shapeStroke: true,
    waypointSvg: waypointPurple,
    waypointSelectedSvg: waypointPurpleSel,
    waypointLowZoomSvg: waypointPurpleDot,
    tint: 'light',
    dbValue: '128,0,128,1',
  },
  {
    key: 'yellow',
    label: 'Yellow',
    color: new Color(255, 255, 0, 1.0),
    rgba: '',
    circleColor: new Color(102, 102, 51, 1.0),
    circleColorRgba: '',
    waypoint: true,
    line: true,
    shapeStroke: true,
    waypointSvg: waypointYellow,
    waypointSelectedSvg: waypointYellowSel,
    waypointLowZoomSvg: waypointYellowDot,
    tint: 'dark',
    dbValue: '255,255,0,1',
  },
  {
    key: 'red',
    label: 'Red',
    color: new Color(255, 0, 0, 1.0),
    rgba: '',
    circleColor: new Color(127, 0, 0, 1.0),
    circleColorRgba: '',
    waypoint: true,
    line: true,
    shapeStroke: true,
    waypointSvg: waypointRed,
    waypointSelectedSvg: waypointRedSel,
    waypointLowZoomSvg: waypointRedDot,
    tint: 'light',
    dbValue: '255,0,0,1',
  },
  {
    key: 'brown',
    label: 'Brown',
    color: new Color(139, 69, 19, 1.0),
    rgba: '',
    circleColor: new Color(83, 41, 11, 1.0),
    circleColorRgba: '',
    waypoint: true,
    line: true,
    shapeStroke: true,
    waypointSvg: waypointBrown,
    waypointSelectedSvg: waypointBrownSel,
    waypointLowZoomSvg: waypointBrownDot,
    tint: 'light',
    dbValue: '139,69,19,1',
  },
  {
    key: 'magenta',
    label: 'Magenta',
    color: new Color(255, 0, 255, 1.0),
    rgba: '',
    circleColor: new Color(128, 0, 128, 1.0),
    circleColorRgba: '',
    waypoint: false,
    line: true,
    shapeStroke: true,
    waypointSvg: null,
    waypointSelectedSvg: null,
    waypointLowZoomSvg: null,
    tint: 'dark',
    dbValue: '255,0,255,1',
  },
  // Specialty colors
  {
    key: 'leupold',
    label: 'Leupold',
    color: new Color(205, 175, 99, 1.0),
    rgba: '',
    circleColor: new Color(143, 122, 69, 1.0),
    circleColorRgba: '',
    waypoint: true,
    line: false,
    shapeStroke: false,
    waypointSvg: waypointLeupold,
    waypointSelectedSvg: waypointLeupoldSel,
    waypointLowZoomSvg: null,
    tint: 'light',
    dbValue: '205,175,99,1',
  },
  {
    key: 'sig-sauer',
    label: 'Sig Sauer',
    color: new Color(0, 160, 222, 1.0),
    rgba: '',
    circleColor: new Color(0, 112, 155, 1.0),
    circleColorRgba: '',
    waypoint: true,
    line: false,
    shapeStroke: false,
    waypointSvg: waypointSigSauer,
    waypointSelectedSvg: waypointSigSauerSel,
    waypointLowZoomSvg: null,
    tint: 'light',
    dbValue: '0,160,222,1',
  },
  {
    key: 'horus',
    label: 'Horus',
    color: new Color(86, 87, 86, 1.0),
    rgba: '',
    circleColor: new Color(255, 115, 1, 1.0),
    circleColorRgba: '',
    waypoint: true,
    line: false,
    shapeStroke: false,
    waypointSvg: waypointHorus,
    waypointSelectedSvg: waypointHorusSel,
    waypointLowZoomSvg: null,
    tint: 'light',
    dbValue: '86,87,86,1',
  },
  {
    key: 'revic',
    label: 'Revic',
    color: new Color(65, 64, 66, 1.0),
    rgba: '',
    circleColor: new Color(255, 255, 255, 1.0),
    circleColorRgba: '',
    waypoint: true,
    line: false,
    shapeStroke: false,
    waypointSvg: waypointRevic,
    waypointSelectedSvg: waypointRevicSel,
    waypointLowZoomSvg: null,
    tint: 'light',
    dbValue: '65,64,66,1',
  },
  {
    key: 'leica',
    label: 'Leica',
    color: new Color(0, 0, 1, 1.0),
    rgba: '',
    circleColor: new Color(255, 255, 255, 1.0),
    circleColorRgba: '',
    waypoint: true,
    line: false,
    shapeStroke: false,
    waypointSvg: waypointLeica,
    waypointSelectedSvg: waypointLeicaSel,
    waypointLowZoomSvg: null,
    tint: 'light',
    dbValue: '0,0,1,1',
  },
]

/**
 * Array of color definitions used for shape fills. Each entry in the array contains the following properties:
 *
 *   - key {String}: Unique key identifying the color. Can be used to look up the color in MARKUP_COLOR_MAP.
 *   - label {String}: Color name (title case).
 *   - opaqueColor {Color}: Fully opaque version of the color value. This is useful for presenting the color as
 *      an option in the UI.
 *   - opaqueRgba {String}: Fully opaque version of the color, represented as an rgba value (suitable for use
 *      in CSS).
 *   - fillColor {Color}: Actual color value used when rendering the shape fill (with transparency),
 *      represented as a Color object (see lib/color.ts)
 *   - fillRgba {String}: Actual fill color (with transparency), represented as an rgba string (suitable for
 *      use in CSS).
 *   - dbValue {String}: How this color is represented on the backend. This must match exactly when
 *      saving/parsing a markup.
 */
export const SHAPE_FILL_COLORS: ShapeFillColor[] = [
  {
    key: 'blue',
    label: 'Blue',
    opaqueColor: new Color(74, 144, 226, 1.0),
    opaqueRgba: '',
    fillColor: new Color(74, 144, 226, 0.1),
    fillRgba: '',
    dbValue: '74,144,226,0.1',
  },
  {
    key: 'orange',
    label: 'Orange',
    opaqueColor: new Color(212, 83, 47, 1.0),
    opaqueRgba: '',
    fillColor: new Color(212, 83, 47, 0.1),
    fillRgba: '',
    dbValue: '212,83,47,0.1',
  },
  {
    key: 'green_brown',
    label: 'Green-Brown',
    opaqueColor: new Color(113, 95, 46, 1.0),
    opaqueRgba: '',
    fillColor: new Color(113, 95, 46, 0.1),
    fillRgba: '',
    dbValue: '113,95,46,0.1',
  },
  {
    key: 'red',
    label: 'Red',
    opaqueColor: new Color(193, 76, 90, 1.0),
    opaqueRgba: '',
    fillColor: new Color(193, 76, 90, 0.1),
    fillRgba: '',
    dbValue: '193,76,90,0.1',
  },
  {
    key: 'yellow',
    label: 'Yellow',
    opaqueColor: new Color(238, 255, 125, 1.0),
    opaqueRgba: '',
    fillColor: new Color(238, 255, 125, 0.1),
    fillRgba: '',
    dbValue: '238,255,125,0.1',
  },
  {
    key: 'blue_green',
    label: 'Blue-Green',
    opaqueColor: new Color(42, 139, 97, 1.0),
    opaqueRgba: '',
    fillColor: new Color(42, 139, 97, 0.1),
    fillRgba: '',
    dbValue: '42,139,97,0.1',
  },
  {
    key: 'white',
    label: 'White',
    opaqueColor: new Color(255, 255, 255, 1.0),
    opaqueRgba: '',
    fillColor: new Color(255, 255, 255, 0.1),
    fillRgba: '',
    dbValue: '255,255,255,0.1',
  },
  {
    key: 'cyan',
    label: 'Cyan',
    opaqueColor: new Color(153, 255, 255, 1.0),
    opaqueRgba: '',
    fillColor: new Color(153, 255, 255, 0.1),
    fillRgba: '',
    dbValue: '153,255,255,0.1',
  },
  {
    key: 'gray',
    label: 'Gray',
    opaqueColor: new Color(80, 80, 80, 1.0),
    opaqueRgba: '',
    fillColor: new Color(80, 80, 80, 0.1),
    fillRgba: '',
    dbValue: '80,80,80,0.1',
  },
  {
    key: 'magenta',
    label: 'Magenta',
    opaqueColor: new Color(232, 118, 255, 1.0),
    opaqueRgba: '',
    fillColor: new Color(232, 118, 255, 0.1),
    fillRgba: '',
    dbValue: '232,118,255,0.1',
  },
  {
    key: 'transparent',
    label: 'None',
    opaqueColor: new Color(0, 0, 0, 0),
    opaqueRgba: '',
    fillColor: new Color(0, 0, 0, 0),
    fillRgba: '',
    dbValue: '0,0,0,0',
  },
]

// Add rgba values
MARKUP_COLORS.forEach((c) => {
  c.rgba = c.color.toRgbaString()
  c.circleColorRgba = c.circleColor.toRgbaString()
})
SHAPE_FILL_COLORS.forEach((c) => {
  c.opaqueRgba = c.opaqueColor.toRgbaString()
  c.fillRgba = c.fillColor.toRgbaString()
})

export const WAYPOINT_COLORS = MARKUP_COLORS.filter(
  (c) => c.waypoint && !SPECIALTY_COLORS.includes(c.key)
)

export const LINE_COLORS = MARKUP_COLORS.filter((c) => c.line)

export const SHAPE_STROKE_COLORS = MARKUP_COLORS.filter((c) => c.shapeStroke)

// transparent

/**
 * Maps color name (e.g., 'blue') to the associated color definition object for regular markup colors (waypoints,
 * lines, and shape strokes).
 */
export const MARKUP_COLOR_MAP = new Map<string, MarkupColor>([
  ...MARKUP_COLORS.map((c) => [c.key, c] as [string, MarkupColor]),
])

/**
 * Maps color dbValue (e.g., '8,122,255,1') to the associated color definition object for regular markup colors
 * (waypoints, lines, and shape strokes).
 */
export const MARKUP_COLOR_DB_VALUE_MAP = new Map<string, MarkupColor>([
  ...MARKUP_COLORS.map((c) => [c.dbValue, c] as [string, MarkupColor]),
])

/**
 * Maps color name (e.g., 'blue') to the associated color definition object for shape fills.
 */
export const SHAPE_FILL_COLOR_MAP = new Map<string, ShapeFillColor>([
  ...SHAPE_FILL_COLORS.map((c) => [c.key, c] as [string, ShapeFillColor]),
])

/**
 * Maps color dbValue (e.g., '8,122,255,1') to the associated color definition object for shape fills.
 */
export const SHAPE_FILL_COLOR_DB_VALUE_MAP = new Map<string, ShapeFillColor>([
  ...SHAPE_FILL_COLORS.map((c) => [c.dbValue, c] as [string, ShapeFillColor]),
])

/**
 * Array of all color keys for both regular markups as well as shape fills. Mainly used for validation in the markup
 * color selector component.
 */
export const ALL_MARKUP_COLOR_KEYS = Array.from(
  new Set([...MARKUP_COLOR_MAP.keys()].concat([...SHAPE_FILL_COLOR_MAP.keys()]))
)

/**
 * Convert color definition to RGBA object
 *
 * @param colorDef - Color definition from getColor() or getDefaultColor()
 * @return RGBA object { r, g, b, a }
 */
export function getRgbaColor(colorDef: MarkupColor): RGBA {
  return colorDef.color.toRgbaObject()
}

/**
 * Convert fill color definition to RGBA object.
 * Falls back to default blue if fillColor doesn't exist.
 *
 * @param colorDef - Fill color definition from getFillColor()
 * @return RGBA object { r, g, b, a }
 */
export function getRgbaFillColor(
  colorDef: ShapeFillColor | null | undefined
): RGBA {
  if (!colorDef?.fillColor) {
    // Return default fill color (blue) if fillColor doesn't exist
    // blue is always present in SHAPE_FILL_COLOR_MAP
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return SHAPE_FILL_COLOR_MAP.get('blue')!.fillColor.toRgbaObject()
  }
  return colorDef.fillColor.toRgbaObject()
}
